import { Close, NotificationsNone } from "@mui/icons-material";
import { Autocomplete, Divider, FormControl, IconButton, TextField } from "@mui/material";
import { ReminderType } from "components/todolist/Reminders/typing";
import { TIME_RANGE_LIST } from "components/todolist/utils/todoUtils";
import { FC } from "react";
import { Control, Controller } from "react-hook-form";

interface ReminderItemProps {
  item: ReminderType;
  onRemove: () => void;
  onUpdate: (item: ReminderType) => void;
  control: Control;
}

const ReminderItem: FC<ReminderItemProps> = ({ item, onRemove, control, onUpdate }) => {
  const genLabel = (item: ReminderType) => {
    if (item.interval === 0) {
      return `Reminder time at ${item.reminderTime.slice(0, 5)}`;
    }
    return `${item.interval} day(s) before at ${item.reminderTime.slice(0, 5)}`;
  };
  return (
    <div
      id={item.id}
      style={{
        border: "1px solid #C9E0FF",
        padding: "16px",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Controller
          name={`${item.id}-template`}
          control={control}
          defaultValue={item.template}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              variant="outlined"
              fullWidth
              autoComplete="off"
              sx={{
                "& .MuiInputBase-root": {
                  height: "40px",
                  borderRadius: "8px",
                },
                "& .MuiInputBase-input": {
                  height: "40px",
                  boxSizing: "border-box",
                  padding: "0 14px",
                },
              }}
              onChange={(e: any) => {
                field.onChange(e);
                onUpdate({ ...item, template: e.target.value });
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : null}
              placeholder="Please fill in the reminder template."
            />
          )}
          rules={{
            required: "The field is required",
          }}
        />

        <IconButton onClick={() => onRemove()}>
          <Close />
        </IconButton>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          <span>Remind</span>
          <Controller
            name={`${item.id}-interval`}
            control={control}
            defaultValue={item.interval}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                variant="outlined"
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    height: "40px",
                    borderRadius: "8px",
                  },
                  "& .MuiInputBase-input": {
                    height: "40px",
                    boxSizing: "border-box",
                    padding: "0 14px",
                  },
                }}
                type="number"
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 1,
                }}
                onChange={(e: any) => {
                  const v = Number(e.target.value);
                  let safeValue = !e.target.value || isNaN(v) || v < 0 ? 0 : v;

                  safeValue = safeValue > 100 ? 100 : safeValue;

                  field.onChange(e);
                  onUpdate({ ...item, interval: safeValue });
                }}
                onKeyDown={(e) => {
                  if (e.key === "." || e.key === "e") e.preventDefault();
                }}
              />
            )}
          />
        </div>
        <div style={{ flex: 1 }}>
          <span>Before</span>
          <Controller
            name={`${item.id}-intervalBy`}
            control={control}
            rules={{
              required: {
                message: "This field is required!",
                value: true,
              },
            }}
            defaultValue={{ label: "Days", value: "day" }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                getOptionLabel={(option) => option.label}
                blurOnSelect={false}
                clearOnBlur={false}
                multiple={false}
                size={"small"}
                disabled={true}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                options={[{ label: "Days", value: "day" }]}
                onChange={(_e: any, value) => {
                  field.onChange(value);
                  onUpdate({ ...item, intervalBy: value?.value });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        height: "40px",
                        boxSizing: "border-box",
                        padding: "0 14px",
                      },
                    }}
                  />
                )}
              />
            )}
          />
        </div>
      </div>
      <div
        style={{
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>At time</span>
        <FormControl
          variant="outlined"
          size="small"
          fullWidth
        >
          <Controller
            name={`${item.id}-reminderTime`}
            control={control}
            rules={{
              required: {
                message: "This field is required!",
                value: true,
              },
            }}
            defaultValue={TIME_RANGE_LIST.find((t) => t.value === item.reminderTime)}
            render={({ field }) => (
              <Autocomplete
                {...field}
                getOptionLabel={(option) => option.label}
                blurOnSelect={false}
                clearOnBlur={false}
                multiple={false}
                size={"small"}
                disabled={false}
                isOptionEqualToValue={(o, v) => o === v}
                options={TIME_RANGE_LIST}
                onChange={(_e: any, value) => {
                  field.onChange(value);
                  onUpdate({ ...item, reminderTime: value?.value });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        height: "40px",
                        boxSizing: "border-box",
                        padding: "0 14px",
                      },
                    }}
                  />
                )}
              />
            )}
          />
        </FormControl>
      </div>
      <Divider />
      <div style={{ display: "flex", alignItems: "center" }}>
        <NotificationsNone />
        <span>{genLabel(item)}</span>
      </div>
    </div>
  );
};

export default ReminderItem;
